import { useState, useCallback } from 'react';

export interface CaseState {
  caseId: string | null;
  status: string | null;
  isProcessing: boolean;
}

export interface UseCaseStateReturn {
  caseState: CaseState;
  setCaseId: (caseId: string | null) => void;
  updateCaseStatus: (status: string) => void;
  setProcessing: (processing: boolean) => void;
  isEnabled: boolean;
  isProcessing: boolean;
}

/**
 * Custom hook for managing case-related state
 */
export const useCaseState = (): UseCaseStateReturn => {
  const [caseState, setCaseState] = useState<CaseState>({
    caseId: null,
    status: null,
    isProcessing: false,
  });

  const setCaseId = useCallback((caseId: string | null) => {
    setCaseState(prev => ({
      ...prev,
      caseId,
      // Reset processing state when setting a new case ID
      isProcessing: false,
    }));
  }, []);

  const updateCaseStatus = useCallback((status: string) => {
    setCaseState(prev => ({
      ...prev,
      status,
      // Determine if case is in a processing state
      isProcessing: isProcessingStatus(status),
    }));
  }, []);

  const setProcessing = useCallback((processing: boolean) => {
    setCaseState(prev => ({
      ...prev,
      isProcessing: processing,
    }));
  }, []);

  // Case is enabled if we have a case ID
  const isEnabled = !!caseState.caseId;

  return {
    caseState,
    setCaseId,
    updateCaseStatus,
    setProcessing,
    isEnabled,
    isProcessing: caseState.isProcessing,
  };
};

/**
 * Determine if a case status represents a processing/non-terminal state
 */
function isProcessingStatus(status: string): boolean {
  const processingStatuses = [
    'INITIAL',
    'PROCESSING_DONE', 
    'SANITIZING',
    'CALCULATING_DIAGNOSES',
    'CREATED', // Initial state when case is just created
    // Add other non-terminal statuses as needed
  ];
  
  return processingStatuses.includes(status.toUpperCase());
}